import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar, Clock, Video, Lock, Sparkles, CheckCircle2, ArrowLeft, ArrowRight, XCircle, Loader2 } from "lucide-react";

/* -------------------- /book -------------------- */
export default function BookPage() {
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(nextWeekday());
  const [slotsResp, setSlotsResp] = useState({ slots: [], duration_min: 0, timezone: "" });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    api.get("/booking/session-types").then((r) => {
      setTypes(r.data);
      if (r.data.length && !selected) {
        // Prefer the admin-marked "default for returning members" (with fallback
        // to the first accessible one — usually the initial consultation).
        const preferred = r.data.find((t) => t.is_default_returning && t.accessible)
          || r.data.find((t) => t.accessible)
          || r.data[0];
        setSelected(preferred);
      }
    });
    api.get("/booking/mine").then((r) => setMine(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected || !selected.accessible) { setSlotsResp({ slots: [] }); return; }
    setLoadingSlots(true);
    setPickedSlot(null);
    api.get(`/booking/slots`, { params: { session_type_id: selected.session_type_id, date } })
      .then((r) => setSlotsResp(r.data))
      .catch(() => setSlotsResp({ slots: [] }))
      .finally(() => setLoadingSlots(false));
  }, [selected, date]);

  const book = async () => {
    if (!pickedSlot) return;
    setRedirecting(true);
    try {
      const { data } = await api.post("/booking/checkout", {
        session_type_id: selected.session_type_id,
        starts_at: pickedSlot,
        origin_url: window.location.origin,
        notes,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Booking failed");
      setRedirecting(false);
    }
  };

  const upcoming = mine.filter((b) => b.status === "confirmed" && new Date(b.starts_at) > new Date());

  return (
    <AppShell>
      <div className="space-y-8">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">1-on-1</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Book a session with Dr. Slava.</h1>
          <p className="text-white/55 mt-2 max-w-2xl">
            Personalized peptide + hormone protocol design. Video via Zoom or Google Meet — link sent after payment.
          </p>
        </div>

        {upcoming.length > 0 && (
          <div className="glass rounded-2xl p-5 border-[#FF8A00]/25">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00] mb-2">Your next session</div>
            {upcoming.map((b) => (
              <div key={b.booking_id} className="flex flex-wrap items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center">
                  <Calendar size={18} />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-display text-lg">{b.session_type_title}</div>
                  <div className="text-sm text-white/60">{fmtDT(b.starts_at)} · {b.duration_min} min</div>
                </div>
                {b.meeting_url ? (
                  <Button asChild className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                    <a href={b.meeting_url} target="_blank" rel="noreferrer">
                      <Video size={14} className="mr-2" /> Join call
                    </a>
                  </Button>
                ) : (
                  <span className="text-xs text-white/45">Meeting link coming from Dr. Slava</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Session type picker */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {types.map((t) => {
            const active = selected?.session_type_id === t.session_type_id;
            return (
              <button
                key={t.session_type_id}
                onClick={() => setSelected(t)}
                data-testid={`session-type-${t.session_type_id}`}
                disabled={!t.accessible}
                className={`text-left rounded-2xl p-6 border transition-all ${
                  active ? "border-[#FF8A00]/50 bg-gradient-to-br from-[#FF8A00]/12 to-[#E52E71]/6"
                         : "border-white/5 bg-[#0A0A0A] hover:border-white/15"
                } ${!t.accessible ? "opacity-55" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-display text-2xl">{t.title}</div>
                    <p className="text-sm text-white/55 mt-2 leading-relaxed">{t.description}</p>
                  </div>
                  {!t.accessible && <Lock size={16} className="text-white/40 shrink-0 mt-1" />}
                </div>
                <div className="mt-4 flex items-center gap-4 flex-wrap">
                  <span className="font-display text-3xl text-brand-gradient">${(t.price_cents/100).toFixed(0)}</span>
                  <span className="text-xs text-white/50 inline-flex items-center gap-1.5">
                    <Clock size={12} /> {t.duration_min} min
                  </span>
                  {!t.accessible && (
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#E52E71]">
                      Requires {t.tier_required}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Date + slots */}
        {selected?.accessible && (
          <div className="glass rounded-2xl p-6 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00]">Pick a date</div>
                <div className="text-xs text-white/45 mt-1">All times shown in {slotsResp.timezone || "your local time"}</div>
              </div>
              <input type="date" value={date} min={today()}
                onChange={(e) => setDate(e.target.value)}
                data-testid="booking-date"
                className="bg-[#0F0F0F] border border-white/10 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-[#FF8A00]/40" />
            </div>

            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 mb-3">Available times</div>
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-sm text-white/50">
                  <Loader2 size={14} className="animate-spin" /> Loading slots…
                </div>
              ) : slotsResp.slots?.length === 0 ? (
                <p className="text-sm text-white/40">No open times on this day. Try another day.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {slotsResp.slots.map((s) => {
                    const active = pickedSlot === s;
                    return (
                      <button key={s} onClick={() => setPickedSlot(s)}
                        data-testid={`slot-${s}`}
                        className={`text-sm py-2 rounded-lg border transition-all ${
                          active ? "bg-gradient-to-r from-[#FF8A00] to-[#E52E71] border-transparent text-white"
                                 : "bg-[#0A0A0A] border-white/8 hover:border-[#FF8A00]/40 text-white/85"
                        }`}>
                        {fmtTime(s, slotsResp.timezone)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {pickedSlot && (
              <>
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 mb-2">Anything Dr. Slava should know? (optional)</div>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="booking-notes"
                    rows={3}
                    className="w-full bg-[#0F0F0F] border border-white/10 rounded-lg px-4 py-3 text-sm text-white/85 focus:outline-none focus:border-[#FF8A00]/40"
                    placeholder="Current protocols, main goals, meds…"/>
                </div>
                <Button
                  onClick={book}
                  disabled={redirecting}
                  data-testid="book-confirm-btn"
                  className="rounded-full px-8 py-6 bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                  {redirecting
                    ? <><Loader2 size={16} className="mr-2 animate-spin" /> Redirecting to Stripe…</>
                    : <>Book & Pay ${(selected.price_cents/100).toFixed(0)} <ArrowRight size={15} className="ml-2" /></>}
                </Button>
                <p className="text-[11px] text-white/40">
                  Full refund if you cancel 24h+ before the session. Non-refundable within 24h.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

/* -------------------- /booking/success -------------------- */
export function BookingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling" });
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) return setState({ status: "error" });
    let n = 0;
    const poll = async () => {
      n++;
      try {
        const { data } = await api.get("/booking/mine");
        const found = data.find((b) => b.stripe_session_id === sessionId && b.status === "confirmed");
        if (found) { setState({ status: "confirmed", booking: found }); return; }
      } catch {}
      if (n > 20) return setState({ status: "timeout" });
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <AppShell>
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          {state.status === "polling" && <>
            <Loader2 size={48} className="mx-auto text-[#FF8A00] animate-spin" />
            <h1 className="font-display text-3xl mt-6">Confirming your booking…</h1>
          </>}
          {state.status === "confirmed" && <>
            <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center pulse-glow">
              <Sparkles size={32} />
            </div>
            <h1 className="font-display text-4xl mt-8">Booked!</h1>
            <p className="text-white/60 mt-3">{state.booking.session_type_title}</p>
            <p className="text-white/80 mt-2">{fmtDT(state.booking.starts_at)}</p>
            <p className="text-xs text-white/45 mt-3">Dr. Slava will send your meeting link ahead of the session.</p>
            <div className="mt-8 flex justify-center gap-2">
              <Button onClick={() => navigate("/book")} className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">My bookings</Button>
              <Button onClick={() => navigate("/dashboard")} variant="outline" className="rounded-full bg-transparent border-white/10">Dashboard</Button>
            </div>
          </>}
          {state.status === "timeout" && <>
            <Loader2 size={48} className="mx-auto text-white/40" />
            <h1 className="font-display text-3xl mt-6">Still processing…</h1>
            <p className="text-white/55 mt-3 text-sm">Your booking will appear in "My sessions" once payment settles.</p>
            <Button onClick={() => navigate("/book")} className="mt-6 rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">Go to bookings</Button>
          </>}
          {state.status === "error" && <>
            <XCircle size={48} className="mx-auto text-[#E52E71]" />
            <h1 className="font-display text-3xl mt-6">Something went wrong</h1>
            <Button onClick={() => navigate("/book")} className="mt-6 rounded-full bg-white/10">Try again</Button>
          </>}
        </div>
      </div>
    </AppShell>
  );
}

/* -------------------- /booking/cancel -------------------- */
export function BookingCancel() {
  const navigate = useNavigate();
  return (
    <AppShell>
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <XCircle size={48} className="mx-auto text-white/50" />
          <h1 className="font-display text-3xl mt-6">Booking cancelled</h1>
          <p className="text-white/55 mt-3 text-sm">No charge was made. Your slot is available again.</p>
          <div className="mt-6 flex justify-center gap-2">
            <Button onClick={() => navigate("/book")} className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">Pick another time</Button>
            <Button onClick={() => navigate("/dashboard")} variant="outline" className="rounded-full bg-transparent border-white/10">Dashboard</Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

/* -------------------- helpers -------------------- */
function today() { return new Date().toISOString().slice(0, 10); }
function nextWeekday() {
  const d = new Date(); d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}
function fmtTime(iso, tz) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: tz || undefined });
  } catch { return iso; }
}
function fmtDT(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
